import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import map from 'lodash/map';
import styled, { css } from 'styled-components';

import EmbedModal, { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import { H3, H4 } from '@ad-scripts/presentation/components/common/headers';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import InsetContainer from '@ad-scripts/presentation/components/common/containers/inset_container';
import IvrTemplateFillIcon from '@ad-scripts/presentation/components/common/icons/fill/ivr-template-fill';
import { IvrTemplate, IvrTemplateList } from 'src/presentation/components/pages/ivr/constants/templates/index';
import NODES_MAPPER from 'src/presentation/components/pages/ivr/constants/nodesMapper';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import { SearchInput } from 'src/presentation/components/agent_desktop_components';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const ADVANCED_NODE_TYPE_LIST = [NODE_TYPES.IVR_NODE_NUMBER_MATCH, NODE_TYPES.IVR_NODE_HTTP];
const NODE_TYPE_LIST = [
  NODE_TYPES.IVR_NODE_SCHEDULE,
  NODE_TYPES.IVR_NODE_MENU,
  NODE_TYPES.IVR_NODE_MESSAGE,

  NODE_TYPES.IVR_NODE_VOICEMAIL,
  NODE_TYPES.IVR_NODE_SMS,
  NODE_TYPES.IVR_NODE_FORWARD,
  NODE_TYPES.IVR_NODE_INPUT,
  NODE_TYPES.IVR_NODE_DIAL,
  NODE_TYPES.IVR_NODE_SCHEDULE_TEMPORARY_SPLIT,

  NODE_TYPES.IVR_NODE_VARIABLE,
  NODE_TYPES.IVR_NODE_VARIABLE_MATCH,
  NODE_TYPES.IVR_NODE_NUMBER_MATCH,
  NODE_TYPES.IVR_NODE_HTTP,
  NODE_TYPES.IVR_NODE_HTTP2,
  NODE_TYPES.IVR_NODE_VOICE_AUTOMATION,
];

const getNodeTypeList = (isHttp2NodeEditEnabled: boolean, isVoiceAIEnabled: boolean) =>
  filter(NODE_TYPE_LIST, nodeType => {
    if (nodeType === NODE_TYPES.IVR_NODE_HTTP2) {
      return isHttp2NodeEditEnabled;
    }
    if (nodeType === NODE_TYPES.IVR_NODE_VOICE_AUTOMATION) {
      return isVoiceAIEnabled;
    }
    return !ADVANCED_NODE_TYPE_LIST.includes(nodeType);
  });

type SelectNodeTypeModalProps = {
  onSelectType: (nodeType: NODE_TYPES) => void;
  onSelectTemplate: (templateId: string) => void;
  onClose: () => void;
  limitNodeTypesTo?: NODE_TYPES[];
};

export const SelectNodeTypeModal: FC<SelectNodeTypeModalProps> = ({
  onClose,
  onSelectType,
  onSelectTemplate,
  limitNodeTypesTo,
}) => {
  const isFeatureEnabled = useIsFeatureEnabled();
  const isHttp2NodeEditEnabled = isFeatureEnabled('enableEditingHttp2Node');
  const isVoiceAIEnabled = isFeatureEnabled('voiceAIEnabled');

  const nodeTypeList = useMemo(
    () => limitNodeTypesTo || getNodeTypeList(isHttp2NodeEditEnabled, isVoiceAIEnabled),
    [isHttp2NodeEditEnabled, isVoiceAIEnabled, limitNodeTypesTo]
  );

  const [searchText, setSearchText] = useState('');
  const [nodeTypes, setNodeTypes] = useState(nodeTypeList);
  const [templateList, setTemplateList] = useState(Object.values(IvrTemplateList));

  useEffect(() => {
    if (!searchText.trim()) {
      setNodeTypes(nodeTypeList);
      setTemplateList(IvrTemplateList);
      return;
    }

    const nodes = filter(nodeTypeList, nodeType => {
      type NodesKeys = keyof typeof NODES_MAPPER;
      type NodesValues = (typeof NODES_MAPPER)[NodesKeys];
      const nodeConfig: NodesValues = NODES_MAPPER[nodeType];
      if (!nodeConfig) {
        return false;
      }
      return (
        nodeConfig.label.toLowerCase().includes(searchText) ||
        nodeConfig.treeNodeLabel.toLowerCase().includes(searchText)
      );
    });

    setNodeTypes(nodes);

    const templates = filter(IvrTemplateList, (template: IvrTemplate) =>
      template.treeNodeLabel.toLowerCase().includes(searchText)
    );
    setTemplateList(templates as typeof IvrTemplateList);
  }, [searchText, setNodeTypes, setTemplateList, nodeTypeList]);

  const onChange = useCallback(
    e => {
      const value = get(e, 'target.value', '');
      setSearchText(value.toLowerCase());
    },
    [setSearchText]
  );

  const selectNodeTypeModalContent = useMemo(() => {
    return (
      <>
        <StyledSearchInput onChange={onChange} />
        {nodeTypes.length ? (
          <NodeTypeListSection
            header="Select the type of node"
            description={<NodeListDescription />}
            nodeTypeList={nodeTypes}
            onSelectType={onSelectType}
          />
        ) : null}
        {templateList.length ? (
          <TemplateListSection onSelectTemplate={onSelectTemplate} templates={templateList} />
        ) : null}
        {!nodeTypes.length && !templateList.length ? <EmptyFilterState searchText={searchText} /> : null}
      </>
    );
  }, [nodeTypes, onChange, onSelectTemplate, onSelectType, searchText, templateList]);
  return (
    <StyledEmbedModal
      data-aid="select-node-type-modal"
      content={selectNodeTypeModalContent}
      onCancel={onClose}
      modalSize={ModalSize.LARGE}
    />
  );
};

const NodeListDescription = () => (
  <span>
    Nodes are used to collect input from customers and route them accordingly.{' '}
    <ExternalLink label="Learn more about nodes" to="https://help.gladly.com/docs/en/what-are-nodes" />
  </span>
);

type SelectSectionType = { 'data-aid': string; header: string; description?: string | JSX.Element };

const SelectSection: FC<SelectSectionType> = ({ 'data-aid': dataAid, header, description, children }) => {
  return (
    <NodeTypesSection data-aid={dataAid}>
      <SectionHeader>{header}</SectionHeader>
      {description && <SectionDescription>{description}</SectionDescription>}
      {children}
    </NodeTypesSection>
  );
};

const EmptyFilterState = ({ searchText }) => {
  return (
    <EmptyStateWrapper>
      <H3>{`No match "${searchText}"`}</H3>
      <P>Check for typos, or try a different search</P>
    </EmptyStateWrapper>
  );
};

type NodeTypeListSectionProps = {
  header: string;
  description: string | JSX.Element;
  nodeTypeList: NODE_TYPES[];
  onSelectType: (nodeType: NODE_TYPES) => void;
};

type TemplateListSectionProps = {
  onSelectTemplate: Function;
  templates: typeof IvrTemplateList;
};

const TemplateListSection: FC<TemplateListSectionProps> = ({ templates, onSelectTemplate }) => {
  return (
    <SelectSection
      data-aid="templates-list"
      header="Select a template"
      description="Pre-created IVRs to help you get started and save time"
    >
      <NodeTypesContainer role="list">
        {map(templates, (template: any) => (
          <NodeType
            color="#cecece"
            data-aid={`nodeType-${template.name}`}
            description={template.description}
            disabled={false}
            icon={StyledIvrTemplateFillIcon}
            iconSize={NodeTypeIconSize.LARGE}
            key={template.id}
            onSelect={() => onSelectTemplate(template.id)}
            treeNodeLabel={template.treeNodeLabel}
          />
        ))}
      </NodeTypesContainer>
    </SelectSection>
  );
};

const NodeTypeListSection = ({ header, description, nodeTypeList, onSelectType }: NodeTypeListSectionProps) => {
  const nodes = map(nodeTypeList, (nodeType, index) => (
    <NodeType
      data-aid={`nodeType[${index}]`}
      disabled={!NODES_MAPPER[nodeType].allowToCreateNew}
      key={nodeType}
      nodeType={nodeType}
      onSelect={onSelectType}
      {...NODES_MAPPER[nodeType]}
    />
  ));

  return (
    <SelectSection data-aid="nodes-list" header={header} description={description}>
      <NodeTypesContainerWrapper>{nodes}</NodeTypesContainerWrapper>
    </SelectSection>
  );
};

const NodeTypeIconSizeDefault = { width: 20, height: 20 };
const NodeTypeIconSizeLARGE = { width: 49, height: 47 };

const NodeTypeIconSize = {
  DEFAULT: NodeTypeIconSizeDefault,
  LARGE: NodeTypeIconSizeLARGE,
};

type NodeTypeProps = {
  'data-aid': string;
  color: string;
  description: string;
  disabled: boolean;
  icon: FC;
  iconSize?: { width: number; height: number };
  nodeType?: NODE_TYPES;
  onSelect: (nodeType) => void;
  treeNodeLabel: string;
};

const NodeType: FC<NodeTypeProps> = ({
  'data-aid': dataAid,
  color,
  description,
  disabled,
  icon,
  iconSize = NodeTypeIconSize.DEFAULT,
  nodeType,
  onSelect,
  treeNodeLabel,
}) => {
  const Icon = icon;
  return (
    <NodeTypeBox
      data-aid={dataAid}
      disabled={disabled}
      onClick={disabled ? null : () => onSelect(nodeType)}
      role="listitem"
    >
      <IconContainer disabled={disabled} color={color} iconSize={iconSize}>
        <Icon />
      </IconContainer>
      <InfoContainer>
        <InfoLabel data-aid="title">{treeNodeLabel}</InfoLabel>
        <InfoDescription data-aid="description">{description}</InfoDescription>
      </InfoContainer>
    </NodeTypeBox>
  );
};

const maxHeightStyles = css`
  max-height: 280px;
`;

const StyledIvrTemplateFillIcon = styled(IvrTemplateFillIcon)`
  fill: ${p => p.theme.colors.gray400};
  stroke: ${p => p.theme.colors.gray400};
  width: 30px;
`;

const P = styled.p`
  margin: 0;
`;

export const EmptyStateWrapper = styled(InsetContainer).attrs({ inset: 'large' })`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${maxHeightStyles}
`;

const NodeTypesSection = styled(InsetContainer)``;

const SectionHeader = styled(H4)`
  text-align: left;
  font-weight: bold;
`;

const SectionDescription = styled(P)`
  color: ${p => p.theme.colors.gray700};
  margin-bottom: 16px;
  text-align: left;
`;

const NodeTypeBox = styled.div`
  align-items: center;
  background: ${p => (p.disabled ? p.theme.colors.gray100 : p.theme.colors.white)};
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  flex-direction: row;
  height: 72px;
  padding: 10px;

  cursor: ${p => (p.disabled ? 'default' : 'pointer')};

  &:hover {
    background: ${p => (p.disabled ? p.theme.colors.gray100 : p.theme.colors.green100)};
  }
`;

const IconContainer = styled.div`
  padding: 10px;

  svg {
    width: ${p => p.iconSize.width}px;
    height: ${p => p.iconSize.height}px;
    fill: ${p => (p.disabled ? p.theme.colors.gray600 : p.color)};
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const InfoLabel = styled.span`
  color: ${p => p.theme.colors.gray900};
  font-weight: bold;
  line-height: 162%;
`;

const InfoDescription = styled.div`
  font-size: 10px;
  overflow: hidden;
  line-height: 162%;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  & > p {
    margin: 0;
  }
`;

const NodeTypesContainer = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  width: 100%;
  overflow-y: auto;

  & > ${NodeTypeBox} {
    width: 30%;
    max-width: 230px;
    flex-grow: 1;

    margin: 5px;

    &:first-child,
    &:nth-child(3n + 1) {
      margin-left: 0;
    }

    &:nth-child(3n) {
      margin-right: 0;
    }
  }
`;

const NodeTypesContainerWrapper = styled(NodeTypesContainer)`
  ${maxHeightStyles}
`;

const StyledEmbedModal = styled(EmbedModal)`
  .modalCard-backdrop {
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.8);
  }

  .modalCard-content {
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.04), 0px 0px 10px rgba(0, 0, 0, 0.06), 0px 0px 80px rgba(0, 0, 0, 0.06);
    min-width: 800px;
    padding: 0 30px;
  }
`;

const StyledSearchInput = styled(SearchInput)`
  flex-shrink: 1;
  margin-right: 12px;
  min-width: 0;
  width: 330px;
`;

export default SelectNodeTypeModal;
